import { ColumnDef } from "@tanstack/react-table";
import { ExportHistoryRecord } from "@/types/payment";
import moment from "moment";
import { useState } from "react";

// Download Button Component
const DownloadButton: React.FC<{ record: ExportHistoryRecord }> = ({ record }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Check if export is completed and not expired
  const isCompleted = record.status === "completed" && record.completed_date;
  // Use expires_on field from the new API structure, fallback to link_expired_ts if available
  const expirationDate = record.expires_on;
  // Check if current time is after the expiration date (including time, not just day)
  const isExpired = expirationDate ? moment().isAfter(moment(expirationDate)) : false;
  const isDownloadable = isCompleted && !isExpired && record.asset_link;
  
  const handleDownload = async () => {
    if (isDownloadable && !isDownloading) {
      setIsDownloading(true);
      try {        
        if (record.asset_link) {
          // Fetch the file and create a blob for direct download
          const response = await fetch(record.asset_link);
          if (!response.ok) {
            throw new Error('Failed to fetch file');
          }
          
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          
          // Create a temporary anchor element to trigger download
          const link = document.createElement('a');
          link.href = url;
          link.download = `export_${record.export_id}.csv`;
          
          // Append to body, click, and remove
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up the blob URL
          window.URL.revokeObjectURL(url);
        } else {
          throw new Error("No download URL available");
        }
      } catch (error) {
        alert("Failed to download export file. Please try again.");
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <div className="flex items-center py-2">
      <div 
        onClick={handleDownload} 
        className={`font-normal leading-normal text-13 ${
          isDownloadable 
            ? 'text-text-primary cursor-pointer hover:underline' 
            : 'text-gray-400 cursor-not-allowed'
        }`}
      >
        {isExpired ? 'Expired' : 'Download'}
      </div>
    </div>
  );
};

export const exportHistoryTableColumns: ColumnDef<ExportHistoryRecord>[] = [
  {
    accessorKey: "export_id",
    header: "Export ID",
    cell: ({ row }) => (
      <div className="py-2">
        <span className="text-text-default font-normal leading-normal text-13 font-mono text-sm">
          {row.getValue("export_id")}
        </span>
      </div>
    ),
  },
  {
    accessorKey: "exported_by",
    header: "Exported by",
    cell: ({ row }) => {
      const record = row.original;
      const empName = record.empName;
      const profileLabel = empName?.label || record.exported_by;
      
      if (!empName?.profileText || !profileLabel || profileLabel === "-") {
        return (
          <div className="py-2">
            <span className="font-normal !leading-normal text-13 text-red-500">-</span>
          </div>
        );
      }
      
      return (
        <div className="py-2">
          <div className="flex items-center gap-1">
            {empName?.profilePic ? (
              <img
                src={empName.profilePic}
                alt={profileLabel}
                className="h-5 w-5 rounded-full object-cover object-center"
                style={{ minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }}
              />
            ) : (
              <div className="flex w-5 h-5 !min-h-5 !min-w-5 items-center justify-center border border-text-primary rounded-full text-text-primary font-bold text-[8px]">
                {empName.profileText}
              </div>
            )}
            <span className="font-normal !leading-normal text-13">
              {profileLabel}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "no_of_rec",
    header: "No. of records",
    cell: ({ row }) => (
      <div className="py-2">
        <span className="text-text-default font-normal leading-normal text-13">
          {row.getValue("no_of_rec")}
        </span>
      </div>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      const statusConfig = {
        completed: { 
          bg: "#f2f9f6", 
          text: "#2c996d", 
          label: "Completed" 
        },
        failed: { 
          bg: "#fef6f6", 
          text: "#ef7778", 
          label: "Failed" 
        },
        expired: { 
          bg: "#f3f4f6", 
          text: "#6b7280", 
          label: "Expired" 
        },
        processing: { 
          bg: "#fffaf3", 
          text: "#ffa82d", 
          label: "Processing" 
        },
      };

      const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.processing;

      return (
        <div className="py-2">
          <span 
            className="inline-flex items-center px-3 py-0.5 rounded-[3px] text-xs font-medium"
            style={{ 
              backgroundColor: config.bg, 
              color: config.text 
            }}
          >
            {config.label}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "start_date",
    header: "Start date",
    cell: ({ row }) => {
      const date = row.getValue("start_date") as string;
      return (
        <div className="py-2">
          <span className="text-text-default font-normal leading-normal text-13">
            {date ? moment(date).format("DD MMM, YYYY") : "-"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "completed_date",
    header: "Completed date",
    cell: ({ row }) => {
      const date = row.getValue("completed_date") as string | null;
      return (
        <div className="py-2">
          <span className="text-text-default font-normal leading-normal text-13">
            {date ? moment(date).format("DD MMM, YYYY") : "-"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "expires_on",
    header: "Expires on",
    cell: ({ row }) => {
      const date = row.getValue("expires_on") as string;
      return (
        <div className="py-2">
          <span className="text-text-default font-normal leading-normal text-13">
            {date ? moment(date).format("DD MMM, YYYY") : "-"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "asset_size",
    header: "File size",
    cell: ({ row }) => {
      const size = row.getValue("asset_size") as number;
      return (
        <div className="py-2">
          <span className="text-text-default font-normal leading-normal text-13">
            {size ? `${(size / 1024).toFixed(1)} KB` : "-"}
          </span>
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "Action",
    cell: ({ row }) => {
      const record = row.original;
      return <DownloadButton record={record} />;
    },
  },
];